/** Asset Mention persistence contract
 * (VISTOERA_CANVAS_UNIFIED_VIDEO_GENERATION_NODE_ASSET_MENTION_KEYFRAME_MODEL_ROUTING_REAL_E2E_P0
 * §12/§15/§16).
 *
 * `generate_video`'s `fields.promptText` (the ONE persisted source of
 * truth for the Prompt Editor, spec §40 persistence) is a plain string
 * that may contain zero or more embedded MENTION MARKERS — never just
 * the human-readable `"[图片1]"` bracket text stored bare (spec §15 "不
 * 要把 prompt persistence 只保存为 '使用图片1'...排序/删除/重新连接以后会产生
 * 歧义"). A marker carries the real `edgeId` (the underlying identity,
 * spec §11/§15 "底层必须引用 asset_id / node_id / edge identity") PLUS a
 * fallback `aliasHint` (what the material was called at the moment it
 * was inserted) so a disconnected mention can still render something
 * meaningful (spec §16 "[图片1 已断开]") even after the live Input
 * Materials list no longer has an entry for that `edgeId` at all.
 *
 * Delimited with U+2063 INVISIBLE SEPARATOR — a character no real
 * keyboard/IME ever produces — so the marker never collides with
 * ordinary typed text (including a user manually typing literal square
 * brackets themselves, which stay perfectly plain and are never
 * mistaken for a mention). */

import type { TFunction } from 'i18next'

const MARK = '\u2063'
const MENTION_RE = /\u2063MENTION:([^:\u2063]+):([^\u2063]*)\u2063/g

export interface ParsedTextSegment {
  type: 'text'
  value: string
}

export interface ParsedMentionSegment {
  type: 'mention'
  edgeId: string
  aliasHint: string
}

export type ParsedPromptSegment = ParsedTextSegment | ParsedMentionSegment

export function makeMentionToken(edgeId: string, aliasHint: string): string {
  return `${MARK}MENTION:${edgeId}:${encodeURIComponent(aliasHint)}${MARK}`
}

export function parsePromptText(text: string): ParsedPromptSegment[] {
  const segments: ParsedPromptSegment[] = []
  let lastIndex = 0
  for (const match of text.matchAll(MENTION_RE)) {
    const idx = match.index ?? 0
    if (idx > lastIndex) segments.push({ type: 'text', value: text.slice(lastIndex, idx) })
    segments.push({ type: 'mention', edgeId: match[1], aliasHint: safeDecode(match[2] || '') })
    lastIndex = idx + match[0].length
  }
  if (lastIndex < text.length) segments.push({ type: 'text', value: text.slice(lastIndex) })
  return segments
}

function safeDecode(v: string): string {
  try {
    return decodeURIComponent(v)
  } catch {
    return v
  }
}

export function hasAnyMention(text: string): boolean {
  return parsePromptText(text).some((s) => s.type === 'mention')
}

export function countDisconnectedMentions(text: string, aliasByEdgeId: Map<string, string>): number {
  return parsePromptText(text).filter((s) => s.type === 'mention' && !aliasByEdgeId.has(s.edgeId)).length
}

/** Remove every inline mention chip for a given edge (Canvas media UX
 * unification §57 — deleting a Reference Asset must not leave dangling
 * Prompt chips). Keeps surrounding plain text intact. */
export function stripMentionsForEdge(text: string, edgeId: string): string {
  if (!text || !edgeId) return text
  return parsePromptText(text)
    .filter((seg) => !(seg.type === 'mention' && seg.edgeId === edgeId))
    .map((seg) => (seg.type === 'text' ? seg.value : makeMentionToken(seg.edgeId, seg.aliasHint)))
    .join('')
}

/** Resolves the persisted marker-bearing `promptText` into the literal
 * string actually submitted as the backend `prompt` field (spec §24
 * "VideoGenerationRequest.instruction") — every mention becomes its
 * CURRENT human-readable `[alias]` (re-resolved fresh at submit time,
 * never a stale cached label), or `[alias 已断开]` if the referenced
 * edge no longer exists (spec §16 — never silently remapped to a
 * different material, never silently dropped). */
export function resolvePromptForSubmit(
  text: string,
  aliasByEdgeId: Map<string, string>,
  t: TFunction<'canvas'>,
): { resolved: string; hasDisconnectedMention: boolean } {
  let hasDisconnectedMention = false
  const resolved = parsePromptText(text)
    .map((seg) => {
      if (seg.type === 'text') return seg.value
      const alias = aliasByEdgeId.get(seg.edgeId)
      if (!alias) {
        hasDisconnectedMention = true
        const fallbackAlias = seg.aliasHint || t('mention.materialFallback')
        return t('mention.disconnectedBracket', { alias: fallbackAlias })
      }
      return `[${alias}]`
    })
    .join('')
  return { resolved, hasDisconnectedMention }
}
